// LenderMatch.js
import React, { useState, useEffect } from 'react';
import { getFirestore, collection, getDocs } from 'firebase/firestore';
import { StarIcon } from '@heroicons/react/20/solid';
import { Dialog, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

// Mock reviews (replace with real data from Firebase)
const mockReviews = {
  "bank1": [
    { id: 1, rating: 5, comment: "Excellent service and quick approval process", author: "John D.", date: "2023-12-01" },
    { id: 2, rating: 4, comment: "Good experience overall, though documentation requirements were extensive", author: "Sarah M.", date: "2023-11-15" },
  ],
  "bank2": [
    { id: 3, rating: 5, comment: "Very professional team and great rates", author: "Michael R.", date: "2023-12-05" },
  ]
};

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function LenderMatch() {
  const [lenders, setLenders] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [selectedLender, setSelectedLender] = useState(null);
  const [isReviewsOpen, setIsReviewsOpen] = useState(false);

  useEffect(() => {
    const fetchLenders = async () => {
      try {
        const db = getFirestore();
        const lendersCollection = collection(db, 'banks');
        const lendersSnapshot = await getDocs(lendersCollection);
        const lendersList = lendersSnapshot.docs.map(doc => ({
          ...doc.data(),
          neededItemsForTermSheet: Array.isArray(doc.data().neededItemsForTermSheet) 
            ? doc.data().neededItemsForTermSheet 
            : []
        }));
        setLenders(lendersList);
      } catch (error) {
        console.error("Error fetching lenders:", error);
      }
    };

    fetchLenders();
  }, []);

  const displayedLenders = showAll ? lenders : lenders.slice(0, 3);

  const renderStars = (rating) => {
    return [...Array(5)].map((_, index) => (
      <StarIcon
        key={index}
        className={classNames(
          index < rating ? 'text-yellow-400' : 'text-gray-300',
          'h-5 w-5 flex-shrink-0'
        )}
      />
    ));
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="bg-white shadow overflow-hidden sm:rounded-lg shadow-md mb-4 border border-gray-300">
        <div className="px-4 py-5 sm:px-6">
          <h2 className="text-lg leading-6 font-medium text-gray-900">
            Find Your Perfect Lending Match
          </h2>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">
            Browse through our network of trusted lenders and find the best match for your business needs.
          </p>
        </div>

        <div className="border-t border-gray-200">
          <div className="divide-y divide-gray-200">
            {displayedLenders.map((lender, index) => (
              <div key={index} className="bg-white overflow-hidden hover:bg-gray-50 transition duration-300">
                <div className="px-4 py-5 sm:px-6">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{lender.bankName}</h3>
                      <p className="text-sm text-gray-600 mb-4">{lender.loanOfficerName}</p>
                    </div>
                    <div 
                      className="flex items-center space-x-2 cursor-pointer hover:opacity-75"
                      onClick={() => {
                        setSelectedLender(lender);
                        setIsReviewsOpen(true);
                      }}
                    >
                      <div className="flex">
                        {renderStars(4)} {/* Replace with actual rating */}
                      </div>
                      <span className="text-sm text-gray-600">
                        (12) {/* Replace with actual count */}
                      </span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                    <div className="space-y-3">
                      <div className="flex justify-between border-b border-gray-200 py-2">
                        <span className="text-gray-600">Loan Range</span>
                        <span className="text-gray-800 font-medium">
                          ${lender.optimalLoanAmountMin?.toLocaleString() || '0'} - ${lender.optimalLoanAmountMax?.toLocaleString() || '0'}
                        </span>
                      </div>

                      <div className="flex justify-between border-b border-gray-200 py-2">
                        <span className="text-gray-600">Rate Range</span>
                        <span className="text-gray-800 font-medium">
                          {lender.usualLoanRate?.min || '0'}% - {lender.usualLoanRate?.max || '0'}%
                        </span>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <div className="flex justify-between border-b border-gray-200 py-2">
                        <span className="text-gray-600">Process Time</span>
                        <span className="text-gray-800 font-medium">{lender.processTime || 'N/A'} days</span>
                      </div>

                      <div className="flex justify-between border-b border-gray-200 py-2">
                        <span className="text-gray-600">Credit Score Min</span>
                        <span className="text-gray-800 font-medium">{lender.creditScore || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="mt-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Match Reasoning:</h4>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                          Strong Match
                        </span>
                        <p className="text-sm text-gray-600">
                          Based on your business profile and loan requirements, this lender has a strong history of approving similar deals.
                        </p>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="inline-flex items-center rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-medium text-blue-800">
                          Industry Expertise
                        </span>
                        <p className="text-sm text-gray-600">
                          Specializes in your industry with dedicated sector expertise.
                        </p>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="inline-flex items-center rounded-full bg-purple-100 px-2.5 py-0.5 text-xs font-medium text-purple-800">
                          Loan Size
                        </span>
                        <p className="text-sm text-gray-600">
                          Your requested amount falls within their preferred lending range.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M10 2c-2.236 0-4.43.18-6.57.524C1.993 2.755 1 4.014 1 5.426v5.148c0 1.413.993 2.67 2.43 2.902 1.168.188 2.352.327 3.55.414.28.02.521.18.642.413l1.713 3.293a.75.75 0 001.33 0l1.713-3.293a.783.783 0 01.642-.413 41.102 41.102 0 003.55-.414c1.437-.232 2.43-1.49 2.43-2.902V5.426c0-1.413-.993-2.67-2.43-2.902A41.289 41.289 0 0010 2zM6.75 6a.75.75 0 000 1.5h6.5a.75.75 0 000-1.5h-6.5zm0 2.5a.75.75 0 000 1.5h3.5a.75.75 0 000-1.5h-3.5z" clipRule="evenodd" />
                      </svg>
                      Notes for Call
                    </h4>
                    <div className="space-y-2">
                      <div className="flex items-start">
                        <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                          <li>Please have your last 3 years of tax returns readily available</li>
                          <li>Be prepared to discuss your experience in the industry</li>
                          <li>Have a clear explanation of how you plan to use the funds</li>
                          <li>Current business financials should be up to date</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-between items-center">
                    <button className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      Meet With Lender
                    </button>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">{lender.email || 'No email'}</p>
                      <p className="text-sm text-gray-600">{lender.number || 'No phone'}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {lenders.length > 3 && (
          <div className="px-4 py-4 sm:px-6 border-t border-gray-200">
            <button
              onClick={() => setShowAll(!showAll)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              {showAll ? 'Show Less' : `Show ${lenders.length - 3} More Lenders`}
            </button>
          </div>
        )}
      </div>

      {/* Reviews Modal */}
      <Transition.Root show={isReviewsOpen} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={setIsReviewsOpen}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 z-10 overflow-y-auto">
            <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                  <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                    <button
                      type="button"
                      className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                      onClick={() => setIsReviewsOpen(false)}
                    >
                      <span className="sr-only">Close</span>
                      <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                    </button>
                  </div>
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      <Dialog.Title as="h3" className="text-lg font-semibold leading-6 text-gray-900">
                        Reviews for {selectedLender?.bankName}
                      </Dialog.Title>
                      <div className="mt-4 space-y-6 max-h-[60vh] overflow-y-auto">
                        {mockReviews[selectedLender?.id || 'bank1']?.map((review) => (
                          <div key={review.id} className="border-b border-gray-200 pb-4">
                            <div className="flex items-center mb-2">
                              <div className="flex">
                                {renderStars(review.rating)}
                              </div>
                              <span className="ml-2 text-sm text-gray-600">
                                {review.date}
                              </span>
                            </div>
                            <p className="text-gray-800 mb-2">{review.comment}</p>
                            <p className="text-sm text-gray-600">- {review.author}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </div>
  );
}